import React, { useMemo } from 'react';
import './TradesTable.css';

interface Trade {
  id: string;
  time: number;
  type: 'buy' | 'sell';
  price: number;
  quantity: number;
  pnl?: number;
  reason?: string;
  commission?: number;
}

interface TradesTableProps {
  trades: Trade[];
  currentTime?: number;
}

const TradesTable: React.FC<TradesTableProps> = ({ trades, currentTime }) => {
  // Calculate cumulative PnL
  const tradesWithCumulativePnL = useMemo(() => {
    let cumulativePnL = 0;
    return trades.map(trade => {
      if (trade.pnl !== undefined) {
        cumulativePnL += trade.pnl;
      }
      return {
        ...trade,
        cumulativePnL,
      };
    });
  }, [trades]);

  // Filter trades up to current time
  const visibleTrades = useMemo(() => {
    if (!currentTime) return tradesWithCumulativePnL;
    return tradesWithCumulativePnL.filter(trade => trade.time <= currentTime);
  }, [tradesWithCumulativePnL, currentTime]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalTrades = visibleTrades.length;
    const profitableTrades = visibleTrades.filter(t => (t.pnl || 0) > 0).length;
    const totalPnL = visibleTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);
    const totalCommission = visibleTrades.reduce((sum, t) => sum + (t.commission || 0), 0);
    const winRate = totalTrades > 0 ? (profitableTrades / totalTrades) * 100 : 0;

    return {
      totalTrades,
      profitableTrades,
      totalPnL,
      totalCommission,
      winRate,
    };
  }, [visibleTrades]);

  const formatDate = (time: number) => {
    return new Date(time * 1000).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPnL = (pnl: number) => {
    const sign = pnl >= 0 ? '+' : '';
    return `${sign}${pnl.toFixed(2)}`;
  };

  return (
    <div className="trades-table-container">
      <div className="trades-stats">
        <div className="stat-card">
          <div className="stat-label">Всего сделок</div>
          <div className="stat-value">{stats.totalTrades}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Прибыльных</div>
          <div className="stat-value positive">{stats.profitableTrades}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Win Rate</div>
          <div className="stat-value">{stats.winRate.toFixed(1)}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Общий PnL</div>
          <div className={`stat-value ${stats.totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {formatPnL(stats.totalPnL)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Комиссии</div>
          <div className="stat-value negative">-{stats.totalCommission.toFixed(2)}</div>
        </div>
      </div>

      <div className="trades-table-wrapper">
        <table className="trades-table">
          <thead>
            <tr>
              <th>Дата/Время</th>
              <th>Тип</th>
              <th>Цена</th>
              <th>Количество</th>
              <th>PnL</th>
              <th>Накопл. PnL</th>
              <th>Комиссия</th>
              <th>Причина</th>
            </tr>
          </thead>
          <tbody>
            {visibleTrades.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-message">
                  Сделки отсутствуют
                </td>
              </tr>
            ) : (
              visibleTrades.map((trade) => (
                <tr key={trade.id} className="trade-row">
                  <td className="trade-time">{formatDate(trade.time)}</td>
                  <td>
                    <span className={`trade-type ${trade.type}`}>
                      {trade.type === 'buy' ? '🟢 BUY' : '🔴 SELL'}
                    </span>
                  </td>
                  <td className="trade-price">{trade.price.toFixed(2)}</td>
                  <td className="trade-quantity">{trade.quantity}</td>
                  <td className={`trade-pnl ${(trade.pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                    {trade.pnl !== undefined ? formatPnL(trade.pnl) : '-'}
                  </td>
                  <td className={`trade-cumulative ${trade.cumulativePnL >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnL(trade.cumulativePnL)}
                  </td>
                  <td className="trade-commission">
                    {trade.commission !== undefined ? trade.commission.toFixed(2) : '-'}
                  </td>
                  <td className="trade-reason">
                    {trade.reason || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TradesTable;
